import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faX } from "@fortawesome/free-solid-svg-icons";
import { isEmpty } from "lodash";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledIconedText,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import { MessageBox, Dropdown, TextInput } from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency, formatCurrency4Decimal, getSearchParams } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";
import { Image } from "../../components/Image";

const BuyerTransactionsPendingList = () => {
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const yesterdayDate = yesterday.toISOString().split('T')[0];
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fOperatorsId: "",
    fCirclesId: "",
    fCategoriesId: "",
    fRechargeNumber: "",
    fFromDate: yesterdayDate,
    fToDate: today,
    fApiSettingsId: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readAPISettings = async () => {
    try {
      const response = await API.get(`/buyer/api-settings`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: apiSettings } = useQuery({
    queryKey: ["buyer_api_settings"],
    queryFn: readAPISettings,
  });

  const readData = async () => {
    try {
      const response = await API.get(
        `/buyer/transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}&fRechargeStatus=Pending&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fApiSettingsId=${appliedFilters.fApiSettingsId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["pending_transactions", paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: readMeta,
  });

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fOperatorsId: "",
      fCirclesId: "",
      fCategoriesId: "",
      fRechargeNumber: "",
      fFromDate: "",
      fToDate: "",
      fApiSettingsId: "",
    });
    setAppliedFilters({
      fOperatorsId: "",
      fCirclesId: "",
      fCategoriesId: "",
      fRechargeNumber: "",
      fFromDate: "",
      fToDate: "",
      fApiSettingsId: "",
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Pending Transactions"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  const operatorsOptions = [
    {
      name: "OPERATOR",
      value: "",
    },
  ];
  !isEmpty(meta?.operators) &&
    meta?.operators?.forEach(({ operatorName, id }) =>
      operatorsOptions?.push({ name: operatorName, value: id })
    );

  const circlesOptions = [
    {
      name: "CIRCLE",
      value: "",
    },
  ];
  !isEmpty(meta?.circles) &&
    meta?.circles?.forEach(({ circleName, id }) =>
      circlesOptions?.push({ name: circleName, value: id })
    );

  const categoriesOptions = [
    {
      name: "CATEGORY",
      value: "",
    },
  ];
  !isEmpty(meta?.categories) &&
    meta?.categories?.forEach(({ category, id }) =>
      categoriesOptions?.push({ name: category, value: id })
    );

  const apiSettingsOptions = [
    {
      name: "API SETTING",
      value: "",
    },
  ];

  if (apiSettings && Array.isArray(apiSettings) && apiSettings.length > 0) {
    apiSettings?.forEach(({ id, name, ipAddress }) =>
      apiSettingsOptions?.push({ name: `${name} ( ${ipAddress} )`, value: id })
    );
  }

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? "show" : ""}>
          <StyledFilters>
            <StyledFilterContainer>
              <Dropdown
                value={filters.fOperatorsId}
                onChange={(value) => handleFilterChange("fOperatorsId", value)}
                placeholder="Operator"
                disabled={isLoading}
                options={operatorsOptions}
              />
              <Dropdown
                value={filters.fCirclesId}
                onChange={(value) => handleFilterChange("fCirclesId", value)}
                placeholder="Circle"
                disabled={isLoading}
                options={circlesOptions}
              />
              <Dropdown
                value={filters.fCategoriesId}
                onChange={(value) => handleFilterChange("fCategoriesId", value)}
                placeholder="Category"
                disabled={isLoading}
                options={categoriesOptions}
              />
              <Dropdown
                value={filters.fApiSettingsId}
                onChange={(value) =>
                  handleFilterChange("fApiSettingsId", value)
                }
                placeholder="API Setting"
                disabled={isLoading}
                options={apiSettingsOptions}
              />
              <TextInput
                value={filters.fRechargeNumber}
                onChange={(value) =>
                  handleFilterChange("fRechargeNumber", value)
                }
                placeholder="Transactions Id"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fFromDate}
                onChange={(value) => handleFilterChange("fFromDate", value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fToDate}
                onChange={(value) => handleFilterChange("fToDate", value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "Operator",
                  dataSelector: "operatorName",
                  dataType: "string",
                  cellrenderer: (value, item) => (
                    <StyledIconedText>
                      <Image
                        source={item.operatorsLogo || "/no-image.png"}
                        alt={value}
                        style={{ width: "50px", height: "50px" }}
                      />
                      {value}
                    </StyledIconedText>
                  ),
                },
                {
                  title: "Recharge Number",
                  dataSelector: "rechargeNumber",
                  dataType: "string",
                },
                {
                  title: "Amount",
                  dataSelector: "rechargeAmount",
                  dataType: "number",
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: "Debit Amount",
                  dataSelector: "debitAmount",
                  dataType: "number",
                  cellrenderer: (value) => formatCurrency4Decimal(value),
                },
                {
                  title: "Margin",
                  dataSelector: "buyersMargin",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => `${value}%`,
                },
                {
                  title: "Status",
                  dataSelector: "rechargeStatus",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === "Success"
                          ? "green"
                          : value === "Failed"
                            ? "red"
                            : "yellow"
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Operators Id",
                  dataSelector: "rechargeOperatorsId",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "Category",
                  dataSelector: "category",
                  dataType: "string",
                },
                // {
                //   title: "Transaction Type",
                //   dataSelector: "transactionType",
                //   dataType: "string",
                // },
                {
                  title: "Circle",
                  dataSelector: "circleName",
                  dataType: "string",
                },
                {
                  title: "Transaction Time",
                  dataSelector: "createdOn",
                  dataType: "string",
                },
                {
                  title: "API Setting Name",
                  dataSelector: "apiSettingName",
                  dataType: "string",
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(BuyerTransactionsPendingList);
